/**
 * Pure functions to dispatch events
 *
 * @date 2024/02/27
 * @note 2026/01/23 - Event-based save flow maintained for backward compatibility.
 * Underlying implementation now includes comprehensive error handling and user notifications.
 * For new code, consider using saveSymbols() directly from symbolSaver.ts
 */

import { TKEvent as EVT, BPSymbol, CreatedSymbolPatch } from '../types';

/** index/ukey patch for a newly-created symbol, keyed by its transient id */
type CreatedSymbolPatchMap = Record<
  string,
  Pick<CreatedSymbolPatch, 'index' | 'ukey'>
>;

/**
 * @deprecated REMOVED - No longer used as of 2026/01/26
 *
 * SymbolEventExtension now dispatches SAVECHANGES directly with full detail
 * (projectId, fileId, pageId, silent), eliminating the need for this relay function.
 *
 * Previous flow: notifySymbolSavingInSilent() -> SAVETOTALCORRECTIONS -> SAVECHANGES
 * New flow: SymbolEventExtension directly dispatches SAVECHANGES with full detail
 *
 * @date 2023/11/23,29
 */
export const notifySymbolSavingInSilent = () => {
  throw new Error(
    'notifySymbolSavingInSilent is deprecated. SymbolEventExtension now dispatches SAVECHANGES directly.',
  );
};

/**
 * Reset global `change` state for symbol through:
 * `useSymbolStore() -> useSymbolsEditStore() -> clearChanges()`
 */
export const muteSymbolsChanged = () => {
  document.dispatchEvent(new Event(EVT.MUTE_SYMBOL_CHANGE));
};

/**
 * Reset modes of the given saved symbols to INI (or remove DEL/RDL ones)
 * without marking the page as changed. Used after a successful in-flight save
 * when the full reload is deferred because the user made new changes.
 *
 * `patches` carries the real index/ukey for symbols that were just created —
 * applied even to symbols outside `ids` (e.g. re-modified mid-save, so not
 * eligible to be muted) to prevent them from being re-created as duplicates
 * on a later save. @2026/08/21
 * @2026/05/19
 */
export const muteSymbolsByIds = (
  pageId: string,
  ids: string[],
  patches?: CreatedSymbolPatchMap,
) => {
  const event = new CustomEvent(EVT.MUTE_SAVED_SYMBOLS, {
    detail: { pageId, ids, patches },
  });
  document.dispatchEvent(event);
};

/**
 * Notify symbol store to refresh page symbols from backend and mute the changes.
 * This is an easy way to keep the symbol store synchronized with backend !
 *
 * @param pageId current page
 * @returns void
 */
export const refreshPageSymbolsAfterSaving = (pageId: string) => {
  // notify symbol store to refresh all the symbols
  const event = new CustomEvent(EVT.REFRESHSYMBOLS, { detail: pageId });
  document.dispatchEvent(event);
};
/**
 * TODO: to use api from editor intead
 * @param pageId
 * @param symbol symbol object
 */
export const addSymbolToStore = (pageId: string, symbol: BPSymbol) => {
  const customEvt = new CustomEvent(EVT.ADDSYMBOL_TO_STORE, {
    detail: { symbol, page: pageId },
  });
  document.dispatchEvent(customEvt);
};

export const clearSymbolsFromStore = (
  pageId: string,
  symbols: (BPSymbol | null)[],
  hasChanged = true,
) => {
  // notify `useFileDetails` to clear cache
  const detail = { pageId, symbols, hasChanged };
  const customEvt = new CustomEvent(EVT.CLEARSYMBOL_FROM_STORE, {
    detail,
  });
  document.dispatchEvent(customEvt);
};

export const clearSymbolFromStore = (
  pageId: string,
  symbolId: string,
  hasChanged = true,
) => {
  // notify `useFileDetails` to clear cache
  const detail = { pageId, symbolId, hasChanged };
  const customEvt = new CustomEvent(EVT.CLEARSYMBOL_FROM_STORE, { detail });
  document.dispatchEvent(customEvt);
};
